import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import SwapWidget from './components/SwapWidget';
import RateDisplay from './components/RateDisplay';
import RecentSwaps from './components/RecentSwaps';
import RateChart from './components/RateChart';
import Icon from '../../components/AppIcon';

const SwapEngineInterface = () => {
  const { userProfile } = useAuth();
  const [exchangeRate, setExchangeRate] = useState(0.0625);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setExchangeRate(prev => Math.max(0.05, Math.min(0.08, prev + (Math.random() - 0.5) * 0.001)));
      setLastUpdate(new Date());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const supportedAssets = [
    { id: 'btc', name: 'Bitcoin', symbol: 'BTC', icon: 'Bitcoin', color: '#F7931A', balance: 0.8542 },
    { id: 'eth', name: 'Ethereum', symbol: 'ETH', icon: 'Hexagon', color: '#627EEA', balance: 5.234 },
    { id: 'usdc', name: 'USD Coin', symbol: 'USDC', icon: 'DollarSign', color: '#10B981', balance: 4520.00 },
    { id: 'card', name: 'Linked Card', symbol: 'CARD', icon: 'CreditCard', color: '#8B5CF6', balance: 1600.03 }
  ];

  const recentSwaps = [
    {
      id: 1,
      fromAsset: 'ETH',
      toAsset: 'USDC',
      fromAmount: 0.5,
      toAmount: 1210.00,
      rate: 2420.00,
      timestamp: '15 min ago',
      status: 'completed',
      fee: 3.63
    },
    {
      id: 2,
      fromAsset: 'BTC',
      toAsset: 'ETH',
      fromAmount: 0.025,
      toAmount: 0.42,
      rate: 16.8,
      timestamp: '1 hour ago',
      status: 'completed',
      fee: 2.50
    },
    {
      id: 3,
      fromAsset: 'USDC',
      toAsset: 'BTC',
      fromAmount: 1000.00,
      toAmount: 0.03,
      rate: 33333.33,
      timestamp: '3 hours ago',
      status: 'completed',
      fee: 3.00
    }
  ];

  const rateHistory = [
    { time: '00:00', rate: 0.0618 },
    { time: '04:00', rate: 0.0622 },
    { time: '08:00', rate: 0.0615 },
    { time: '12:00', rate: 0.0628 },
    { time: '16:00', rate: 0.0621 },
    { time: '20:00', rate: 0.0625 },
    { time: 'Now', rate: exchangeRate }
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Swap Engine</h1>
              <p className="text-muted-foreground">Convert between BTC, ETH, Stablecoins, and Card balance</p>
            </div>
            {userProfile && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Monthly Swap Limit</p>
                <p className="text-2xl font-bold text-foreground">{userProfile?.monthly_swap_limit || 0}</p>
                {userProfile?.tier === 'pro' && (
                  <div className="inline-flex items-center gap-1 mt-1 px-2 py-1 bg-primary/20 text-primary rounded text-xs font-semibold">
                    <Icon name="Crown" size={12} />
                    Pro Tier
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <SwapWidget 
                assets={supportedAssets} 
                exchangeRate={exchangeRate}
                lastUpdate={lastUpdate}
              />
            </div>
            <div>
              <RateDisplay 
                exchangeRate={exchangeRate} 
                lastUpdate={lastUpdate}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RateChart data={rateHistory} />
            <RecentSwaps swaps={recentSwaps} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SwapEngineInterface;